import React, { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { uploadAdminImageFile } from '../Config/adminOrderFlowService';

function labelClass() {
  return 'block text-sm font-medium text-gray-700 mb-1';
}

/**
 * Upload one or many images (Supabase); returns public URLs for order-flow payloads (`start_photos`, `end_photos`, etc.).
 * Pass `busy` + `onBusyChange` for parent-controlled busy state; omit both to manage busy internally.
 */
export default function OrderFlowImageUploadList({
  label,
  hint,
  urls,
  onUrlsChange,
  disabled,
  busy: busyProp,
  onBusyChange,
}) {
  const inputRef = useRef(null);
  const [internalBusy, setInternalBusy] = useState(false);
  const busyControlled = busyProp !== undefined && typeof onBusyChange === 'function';
  const uploading = busyControlled ? busyProp : internalBusy;

  const setUploading = (v) => {
    if (busyControlled) onBusyChange(v);
    else setInternalBusy(v);
  };

  const list = Array.isArray(urls) ? urls : [];

  const handleFiles = async (e) => {
    // Snapshot before clearing — input.files is a live FileList; setting value='' empties it.
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length || disabled || uploading) return;
    setUploading(true);
    try {
      const appended = [];
      for (const file of files) {
        const type = String(file?.type || '').toLowerCase();
        const name = String(file?.name || '').toLowerCase();
        const looksLikeImage =
          type.startsWith('image/') ||
          (!type && /\.(jpe?g|png|webp|gif|heic|heif|bmp|tiff?)$/i.test(name));
        if (!looksLikeImage) {
          toast.error(`Skipped (not an image): ${file.name}`);
          continue;
        }
        appended.push(await uploadAdminImageFile(file));
      }
      if (appended.length) {
        onUrlsChange([...list, ...appended]);
        toast.success(
          appended.length === 1 ? 'Image uploaded' : `${appended.length} images uploaded`
        );
      }
    } catch (err) {
      toast.error(err?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="sm:col-span-2">
      <label className={labelClass()}>{label}</label>
      <div className="mt-2 flex flex-wrap items-end gap-3">
        {list.map((url, i) => (
          <div
            key={`${url}-${i}`}
            className="relative h-20 w-20 overflow-hidden rounded-lg border border-gray-200 bg-gray-100"
          >
            <img src={url} alt="" className="h-full w-full object-cover" />
            <button
              type="button"
              disabled={disabled}
              onClick={() => onUrlsChange(list.filter((_, j) => j !== i))}
              className="absolute right-0 top-0 rounded-bl bg-black/60 px-1.5 py-0.5 text-xs font-bold text-white hover:bg-black/80 disabled:opacity-50"
              aria-label="Remove image"
            >
              ×
            </button>
          </div>
        ))}
        <div>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={handleFiles}
            disabled={disabled || uploading}
          />
          <button
            type="button"
            disabled={disabled || uploading}
            onClick={() => inputRef.current?.click()}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-800 shadow-sm hover:bg-gray-50 disabled:opacity-50"
          >
            {uploading ? 'Uploading…' : 'Add photos'}
          </button>
        </div>
      </div>
      {hint && list.length === 0 ? <p className="mt-1 text-xs text-gray-500">{hint}</p> : null}
    </div>
  );
}
